import { useTheme } from '@billy/design-system'
import { trackError } from '@billy/track-error'
import type { VirtualAssistantHostConfig } from '@shinetools/virtual-assistant-client'
import { useMemo } from 'react'

import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useCurrentUser } from '@modules-deprecated/app/user'

import { getSupportPhone } from '../../../constants/supportPhone'
import { useIntercom } from '../../../contexts/intercomContext'
import { Scope } from '../../../enums/scope'
import { useSegment } from '../../../hooks'
import { useCopyAndNotify } from '../../../hooks/useCopyAndNotify'
import { useIsHelpCenterEnabled } from '../../../hooks/useIsHelpCenterEnabled'
import { getCurrentLocale } from '../../../utils'
import { apiUrl, getAccessToken } from '../../../utils/api'
import { isAuthorized } from '../../../utils/isAuthorized'
import { subscribeToPathnameChange } from '../utils/subscribeToPathnameChange'
import { useVirtualAssistantNavigate } from './useVirtualAssistantNavigate'

export const useVirtualAssistantHostConfig = (): VirtualAssistantHostConfig => {
  const { organization } = useUserOrganization()
  const { user } = useCurrentUser()
  const { showConversation, showMessages, showNewMessage, unreadConversationsCount } = useIntercom()
  const {
    ds: { data },
  } = useTheme()
  const { track } = useSegment()
  const { copy } = useCopyAndNotify()
  const { isHelpCenterEnabled } = useIsHelpCenterEnabled()
  const { navigate: navigateToTarget } = useVirtualAssistantNavigate()

  const countryId = organization?.countryId
  const locale = getCurrentLocale()
  const isSupportPhoneVisible = isAuthorized([Scope.SupportPhoneNumberRead])
  const supportEmail = data.menu.help.email

  return useMemo(
    () => ({
      apiUrl: apiUrl ?? '',
      copyToClipboard: copy,
      countryId,
      getAccessToken,
      intercom: {
        openConversation: showConversation,
        openMessages: showMessages,
        openNewMessage: showNewMessage,
        unreadConversationsCount,
      },
      isHelpCenterEnabled,
      locale,
      navigate: navigateToTarget,
      organizationId: organization?.id,
      subscribeToPathnameChange,
      support: {
        email: supportEmail,
        isPhoneVisible: isSupportPhoneVisible,
        phone: getSupportPhone(countryId),
      },
      trackError,
      trackEvent: track,
      user: user ? { email: user.email, globalId: user.globalId, name: user.name } : undefined,
    }),
    [
      copy,
      countryId,
      isHelpCenterEnabled,
      isSupportPhoneVisible,
      locale,
      navigateToTarget,
      organization?.id,
      showConversation,
      showMessages,
      showNewMessage,
      supportEmail,
      track,
      unreadConversationsCount,
      user,
    ],
  )
}
